package com.islamicmedia.shared.domain.model

/**
 * "Request additional searches": what a signed-in person stopped by the WEEKLY analysis limit can
 * send from the limit dialog (POST /api/limit-request). The text is saved and emailed to support,
 * and the owner answers by hand; nothing is granted automatically.
 *
 * Like [DailyLimitNotice], the copy lives here, not in the UIs, so the web and mobile dialogs say
 * exactly the same thing. No em dashes anywhere in user-facing copy.
 */
object LimitRequestCopy {
    /** The secondary button under the limit dialog's OK. */
    const val BUTTON = "Request additional searches"

    const val TITLE = "Request additional searches"
    const val INTRO = "Tell us why you need more searches this week. We'll reply by email."
    const val PLACEHOLDER = "For example: I'm checking books for my children's school library"

    const val SEND = "Send"
    const val SENDING = "Sending..."
    const val CANCEL = "Cancel"

    /** Shown in place of the form once the server has the request. */
    const val SENT = "Thanks! We've received your request and will get back to you by email."
    const val OK = "OK"

    /** Fewest and most characters (after trimming) the server accepts. Send stays disabled below the first. */
    const val MIN_CHARS = 10
    const val MAX_CHARS = 1000

    /** Inline text when the server gave none (offline, a gateway error, an old server). */
    const val GENERIC_ERROR = "We couldn't send your request. Please check your connection and try again."

    /** Inline text if Send is somehow pressed with too little written (the server's own words for it). */
    const val TOO_SHORT = "Please write a few words (10 to 1000 characters)."

    /** Inline text for a second request inside 24 hours, when the server's message did not come through. */
    const val ALREADY_REQUESTED = "You've already sent a request today. We'll reply by email."

    /** Inline text when the hourly limiter turned the request away. */
    const val TOO_MANY = "Too many attempts. Please try again in a little while."

    /** Is [text] long enough to send? Counted after trimming, the way the server counts. */
    fun canSend(text: String): Boolean = text.trim().length >= MIN_CHARS
}

/** What the limit dialog's request form is doing. Hidden = the normal limit dialog is showing. */
sealed interface LimitRequestUiState {
    /** The request form is not open. */
    data object Hidden : LimitRequestUiState

    /** The form is open and waiting for the person to write and press Send. */
    data object Editing : LimitRequestUiState

    /** The request is on its way. The form stays up, with Send disabled. */
    data object Sending : LimitRequestUiState

    /** The server has the request: the thank-you message replaces the form. */
    data object Sent : LimitRequestUiState

    /** The request was not sent; [message] is shown inline and the form stays editable. */
    data class Error(val message: String) : LimitRequestUiState
}

/** The outcome of POST /api/limit-request. */
sealed interface LimitRequestResult {
    /** Saved (the support email may still have failed; that is the server's problem, not the person's). */
    data object Sent : LimitRequestResult

    /** Already sent one in the last 24 hours (HTTP 429, `already_requested`). */
    data class AlreadyRequested(val message: String) : LimitRequestResult

    /** The text was too short or too long (HTTP 400, `reason_length`). */
    data class Invalid(val message: String) : LimitRequestResult

    /** Anything else: offline, not signed in, a server error. [message] is the server's own words when it gave any. */
    data class Failed(val message: String? = null) : LimitRequestResult
}

/** The inline text for a result that did not send, or null for [LimitRequestResult.Sent]. */
fun LimitRequestResult.errorMessage(): String? = when (this) {
    LimitRequestResult.Sent -> null
    is LimitRequestResult.AlreadyRequested -> message
    is LimitRequestResult.Invalid -> message
    is LimitRequestResult.Failed -> message?.takeIf { it.isNotBlank() } ?: LimitRequestCopy.GENERIC_ERROR
}
